// React
import PropTypes from "prop-types";
import { useContext, useEffect, useRef, useState } from "react";

// @mui material components
import { Card, CircularProgress, Collapse, Fade, Grid, Icon, Modal, Tooltip } from "@mui/material";

import { gql, useLazyQuery, useMutation } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import Tesseract from "tesseract.js";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "40%", xxl: "30%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalDocumentacionAdd({ open, handleClose, idPermiso, refetch }) {
  const { handleSnackbar } = useContext(MessageManager);
  const [documento, setDocumento] = useState();
  const inputRef = useRef();
  const [file, setFile] = useState();
  const [ocrText, setOcrText] = useState("");
  const [ocrLoading, setOcrLoading] = useState(false);

  const [getPermiso, { data, loading }] = useLazyQuery(gql`
    query getPermiso($id: ID!) {
      permisoGob(id: $id) {
        medioDePago
        tipo
      }
    }
  `);

  const [addPermisoFile, { loading: loadingFile }] = useMutation(gql`
    mutation addPermisoFile(
      $idPermiso: ID!
      $file: Upload!
      $nombre: String!
      $ocrId: String
      $tipo: String
    ) {
      addPermisoFile(
        idPermiso: $idPermiso
        file: $file
        nombre: $nombre
        ocrId: $ocrId
        tipo: $tipo
      ) {
        id
      }
    }
  `);

  const handleOCRProcess = (file) => {
    setOcrLoading(true);

    if (file.type === "application/pdf") {
      setOcrText(`${file.name.toLowerCase().replace(/\s/g, "")}-${file.size}`);
      setOcrLoading(false);
    } else if (file.type.includes("image")) {
      const fileURL = URL.createObjectURL(file);
      Tesseract.recognize(
        fileURL,
        "spa" // Idioma del OCR (español en este caso)
      )
        .then(({ data: { text } }) => {
          const textNormalized = text.toLowerCase().replace(/\s/g, "");
          setOcrText(textNormalized);
        })
        .catch((err) => {
          console.error("Error OCR:", err);
        })
        .finally(() => {
          setOcrLoading(false);
          URL.revokeObjectURL(fileURL);
        });
    } else {
      handleSnackbar("El archivo debe ser una imagen o un PDF", "error");
      setOcrLoading(false);
      setFile(null);
    }
  };

  useEffect(() => {
    setDocumento(null);
    setFile(null);
    setOcrText("");
    setOcrLoading(false);
    if (idPermiso && open) {
      getPermiso({ variables: { id: idPermiso } });
    }
  }, [idPermiso, open]);
  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">
                  Agregar comprobante de pago acompañante
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox py={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <SoftBox display="flex" flexDirection="column" gap={1}>
                    <SoftTypography variant="h7">
                      Deberá realizar una transferencia bancaria o generar un volante de pago. Para
                      la transferencia debe realizar una transferencia de{" "}
                      <strong>$14.000,00 (catorce mil pesos)</strong> a la siguiente cuenta:
                    </SoftTypography>
                    <SoftBox display="flex" flexDirection="column" gap={1}>
                      <SoftTypography variant="h7">
                        <b>Banco:</b> Banco Nación
                      </SoftTypography>
                      <SoftTypography variant="h7">
                        <b>CBU:</b> 0110478720047801141727
                      </SoftTypography>
                    </SoftBox>
                    <SoftTypography variant="h7">
                      O puedes acceder al volante de pago desde el siguiente enlace:
                    </SoftTypography>
                    <SoftButton
                      color="gobPrimary"
                      fullWidth
                      target="_blank"
                      href="https://sistematributario.dpip.sanluis.gov.ar/ords/clavefiscal/r/clfpub/emitir-tasa-administrativa?p41_tasa_codigo=C883&clear=41"
                    >
                      Generar boleta de pago&nbsp;
                      <Icon>arrow_forward</Icon>
                    </SoftButton>
                  </SoftBox>
                </Grid>
                <Grid item xs={12}>
                  <SoftButton
                    color={file ? "success" : "gobSecondary"}
                    fullWidth
                    onClick={() => inputRef.current.click()}
                    disabled={loadingFile}
                  >
                    {ocrLoading
                      ? "Procesando archivo..."
                      : file
                      ? file.name.length > 40
                        ? `${file.name.substring(0, 40)}...${file.name.substring(
                            file.name.length - 4
                          )}`
                        : file.name
                      : "Subir comprobante de pago acompañante"}
                    &nbsp;
                    <Icon sx={{ fontWeight: "light" }}>cloud_upload</Icon>
                  </SoftButton>
                  <input
                    ref={inputRef}
                    type="file"
                    style={{ display: "none" }}
                    accept=".pdf, .jpg, .jpeg, .png"
                    onChange={(e) => {
                      if (e.target.files[0] && e.target.files[0].size < 5000000) {
                        setFile(e.target.files[0]);
                        handleOCRProcess(e.target.files[0]);
                      } else {
                        handleSnackbar("El archivo debe pesar menos de 5MB", "error");
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </SoftBox>
            <Collapse in={file}>
              <SoftButton
                color="gobSecondary"
                fullWidth
                disabled={loadingFile || !idPermiso || !file || ocrLoading}
                onClick={() => {
                  addPermisoFile({
                    variables: {
                      idPermiso,
                      file: file,
                      nombre: "Comprobante de pago acompañante",
                      ocrId: ocrText,
                      tipo: "Comprobante de pago acompañante",
                    },
                  })
                    .then(() => {
                      handleSnackbar("Archivo subido correctamente", "success");
                      refetch();
                      handleClose();
                      setFile(null);
                      setDocumento(null);
                    })
                    .catch(() => {
                      handleSnackbar("Error al subir el archivo", "error");
                    });
                }}
              >
                Enviar para revisión&nbsp;
                {loadingFile ? (
                  <CircularProgress
                    size={15}
                    sx={{
                      marginRight: 0.5,
                    }}
                    color="inherit"
                  />
                ) : (
                  <Icon sx={{ fontWeight: "light" }}>save</Icon>
                )}
              </SoftButton>
            </Collapse>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalDocumentacionAdd.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idPermiso: PropTypes.string,
  refetch: PropTypes.func,
};
